import {
  BookMarked,
  Heart,
  LayoutDashboard,
  Library,
  LogOut,
  ScanLine,
  Settings,
  SunMoon,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router';

import { useLogout } from '../features/auth/session';
import { useOutboxSync } from '../features/scan/outbox-store';
import { useScanQueueCount } from '../features/scan/queries';
import { useWedgeScanner } from '../features/scan/useWedgeScanner';
import { useTheme } from '../lib/theme';
import { cn } from '../lib/utils';
import { PwaStatus } from './PwaStatus';
import { Button } from './ui/button';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/collection', labelKey: 'nav.collection', icon: Library },
  { to: '/series', labelKey: 'nav.series', icon: BookMarked },
  { to: '/scan', labelKey: 'nav.scan', icon: ScanLine },
  { to: '/wishlist', labelKey: 'nav.wishlist', icon: Heart },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings },
];

/** Bottom navigation on phones, sidebar from md upwards. */
export function AppShell() {
  const { t } = useTranslation();
  const { toggleTheme } = useTheme();
  const logout = useLogout();
  const navigate = useNavigate();
  const queueCount = useScanQueueCount();

  // Batch scans made offline go out as soon as there is a connection.
  useOutboxSync();

  // A handscanner works from every page: the scan opens the scan page.
  useWedgeScanner(
    (code) => void navigate('/scan', { state: { wedgeCode: code, nonce: Date.now() } }),
  );

  const badgeFor = (to: string) =>
    to === '/scan' && queueCount.data?.open ? (
      <span
        className="ml-auto rounded-full bg-accent px-1.5 text-[0.65rem] font-semibold text-accent-foreground"
        title={t('scan.queue.openBadge', { count: queueCount.data.open })}
      >
        {queueCount.data.open}
      </span>
    ) : null;

  return (
    <div className="min-h-dvh bg-background md:grid md:grid-cols-[16rem_1fr]">
      <aside className="sticky top-0 hidden h-dvh flex-col border-r bg-surface px-4 py-6 md:flex">
        <div className="px-2">
          <p className="font-display text-2xl leading-none tracking-wide text-foreground uppercase">
            {t('app.name')}
          </p>
          <p className="mt-1 text-xs text-muted">{t('app.tagline')}</p>
        </div>

        <nav className="mt-8 flex flex-1 flex-col gap-1">
          {NAV_ITEMS.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                  isActive
                    ? 'bg-surface-raised text-foreground'
                    : 'text-muted hover:bg-surface-raised hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={cn('size-4', isActive && 'text-accent')} />
                  {t(labelKey)}
                  {badgeFor(to)}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex gap-2 border-t pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            aria-label={t('nav.toggleTheme')}
            title={t('nav.toggleTheme')}
          >
            <SunMoon className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="flex-1 justify-start"
          >
            <LogOut className="size-4" />
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      <div className="flex min-h-dvh flex-col">
        <header className="flex items-center justify-between border-b halftone px-4 py-3 md:hidden">
          <p className="font-display text-xl tracking-wide text-foreground uppercase">
            {t('app.name')}
          </p>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={t('nav.toggleTheme')}
            >
              <SunMoon className="size-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              aria-label={t('nav.logout')}
            >
              <LogOut className="size-5" />
            </Button>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 pb-24 md:px-8 md:py-8 md:pb-8">
          <Outlet />
        </main>

        <PwaStatus />

        <nav className="fixed right-0 bottom-0 left-0 z-10 grid grid-cols-6 border-t bg-surface pb-[env(safe-area-inset-bottom)] md:hidden">
          {NAV_ITEMS.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex min-h-14 flex-col items-center justify-center gap-1 text-[0.625rem]',
                  isActive ? 'text-accent' : 'text-muted',
                )
              }
            >
              <Icon className="size-5" />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
